import type { RobotMediaItem } from '@/services/api/domains/robots.api'
import { ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useTileMediaUrl } from '../RobotMedia/useTileMediaUrl'

interface PictureThumbButtonProps {
  media: RobotMediaItem
  onOpen: (media: RobotMediaItem) => void
}

/**
 * A stored picture as the log's 48 px square: the thumbnail, and a click that opens the original.
 * Shared by the "Picture taken" row and an alarm row whose photo is in the fetched media, so a
 * picture looks and opens the same whichever row it sits on.
 */
export function PictureThumbButton({ media, onOpen }: PictureThumbButtonProps) {
  const { t } = useTranslation('console')
  const { tileRef, url, status, handleLoad, handleError } = useTileMediaUrl<HTMLButtonElement>(media)

  return (
    <button
      ref={tileRef}
      type="button"
      onClick={() => onOpen(media)}
      aria-label={t('alerts.log.viewPicture', 'View picture')}
      className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200 dark:bg-white/5 dark:ring-white/10"
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className="h-full w-full object-cover"
        />
      ) : status === 'failed' ? (
        <ImageOff
          className="h-5 w-5 text-gray-400 dark:text-gray-500"
          aria-label={t('alerts.log.pictureUnavailable', "Couldn't load this picture")}
        />
      ) : (
        <span aria-busy="true" className="h-full w-full animate-pulse bg-gray-200 dark:bg-white/10" />
      )}
    </button>
  )
}
